import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileText, Send, Calendar, Eye, Edit, Printer, Trash2, Search, CheckCircle, ArrowUpDown, ArrowUp, ArrowDown, CreditCard, ExternalLink, Download, MoreHorizontal, CalendarPlus } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useApp } from '../contexts/AppContext';
import CreateInvoiceModal from '../components/CreateInvoiceModal';
import GenerateMonthlyModal from '../components/GenerateMonthlyModal';
import { useBranding } from '../contexts/BrandingContext';
import { getContrastText } from '../utils/colorContrast';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Invoices = () => {
  const { t } = useApp();
  const { branding } = useBranding();
  const { selectedBranch } = useApp();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [customers, setCustomers] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printSizeDialogOpen, setPrintSizeDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pendingPrintInvoice, setPendingPrintInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Local input state

  // Auto-fill search from URL ?customer=<account_number> (from Customers page "Edit Tagihan")
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const customerParam = params.get('customer');
    if (customerParam) {
      setSearchInput(customerParam);
      setSearchTerm(customerParam);
    }
    // Honor ?status= from URL (used by dashboard SecondaryKPIRow click-through)
    const statusParam = params.get('status');
    if (statusParam) {
      const allowed = ['paid', 'pending', 'overdue', 'cancelled', 'ALL'];
      if (allowed.includes(statusParam)) {
        setStatusFilter(statusParam);
      }
    }
  }, [location.search]);
  const [susulanFilter, setSusulanFilter] = useState(false);
  const [statusFilter, setStatusFilter] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const s = sp.get('status');
      return ['paid', 'pending', 'overdue', 'cancelled'].includes(s) ? s : 'ALL';
    } catch { return 'ALL'; }
  });
  const [periodMonthFilter, setPeriodMonthFilter] = useState('ALL'); // 1..12 atau ALL
  const [periodYearFilter, setPeriodYearFilter] = useState('ALL');
  const [ownerFilter, setOwnerFilter] = useState('ALL');
  const [owners, setOwners] = useState([]);
  const [printSize, setPrintSize] = useState('a4');
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  const [selectedStatusInvoice, setSelectedStatusInvoice] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [editFormData, setEditFormData] = useState({
    created_at: '',
    due_date: '',
    payment_date: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Bulk delete states
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  // 2026-06-12: bulk mark-as-paid
  const [bulkPaidDialogOpen, setBulkPaidDialogOpen] = useState(false);
  const [bulkPaidLoading, setBulkPaidLoading] = useState(false);
  const [bulkPaidAccounts, setBulkPaidAccounts] = useState({ cash: [], revenue: [], defaultCash: null, defaultRevenue: null });
  const [bulkPaidForm, setBulkPaidForm] = useState({ cashCode: '', revenueCode: '', paymentMethod: 'cash', paidDateMode: 'due_date' });
  const [bulkPaidResult, setBulkPaidResult] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');
  
  // Payment states
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState(null);
  
  // Extend due date states
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [extendInvoice, setExtendInvoice] = useState(null);
  const [extendFormData, setExtendFormData] = useState({
    new_due_date: '',
    reason: ''
  });

  useEffect(() => {
    // 2026-08-08 FIX: AbortController supaya fetch lama di-cancel kalau ada state
    // change baru (mencegah race condition di mana fetch tanpa search selesai
    // sesudah fetch dengan search, dan overwrite hasil yang benar dengan yang salah).
    const controller = new AbortController();
    fetchData({ signal: controller.signal });
    return () => controller.abort();
  }, [currentPage, itemsPerPage, searchTerm, statusFilter, susulanFilter, ownerFilter, periodMonthFilter, periodYearFilter, selectedBranch, sortConfig]);

  // Fetch owners for filter dropdown
  const fetchOwners = async () => {
    try {
      const authToken = localStorage.getItem('token');
      if (!authToken) return;
      
      // If user is owner role, only show their own owner in filter
      const userRole = user?.role?.toLowerCase();
      if (userRole === 'owner') {
        // For owner role, set filter to their owner_id and don't fetch all owners
        if (user?.owner_id) {
          setOwners([{ id: user.owner_id, name: user.full_name || user.username }]);
          setOwnerFilter(String(user.owner_id));
        }
        return;
      }
      
      const params = new URLSearchParams();
      if (selectedBranch && selectedBranch !== 'all') {
        params.append('branch_id', selectedBranch);
      }
      const response = await axios.get(`${API_URL}/api/v2/owners?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (response.data.success) {
        setOwners(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching owners:', error);
    }
  };

  useEffect(() => {
    const authToken = localStorage.getItem('token');
    if (authToken && user) {
      const userRole = user?.role?.toLowerCase();
      if (userRole === 'owner' && user?.owner_id) {
        // For owner role, auto-set their owner filter
        setOwners([{ id: user.owner_id, name: user.full_name || user.username }]);
        setOwnerFilter(String(user.owner_id));
      } else {
        fetchOwners();
        setOwnerFilter('ALL');
      }
    }
  }, [selectedBranch, user]);

  const fetchData = async ({ silent = false, signal = null } = {}) => {
    try {
      if (!silent) setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build query params for server-side pagination
      const params = new URLSearchParams({
        page: currentPage,
        per_page: itemsPerPage
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      if (statusFilter && statusFilter !== 'ALL') {
        params.append('status', statusFilter.toLowerCase());
      }
      if (susulanFilter) {
        params.append('is_susulan', 'true');
      }
      if (ownerFilter && ownerFilter !== 'ALL') {
        params.append('owner_id', ownerFilter);
      }
      if (periodMonthFilter && periodMonthFilter !== 'ALL') {
        params.append('period_month', periodMonthFilter);
      }
      if (periodYearFilter && periodYearFilter !== 'ALL') {
        params.append('period_year', periodYearFilter);
      }
      
      // Add branch filter if selected
      if (selectedBranch && selectedBranch !== 'all') {
        params.append('branch_id', selectedBranch);
      }
      
      // Add sort parameters
      if (sortConfig.key) {
        params.append('sort_by', sortConfig.key);
        params.append('sort_order', sortConfig.direction);
      }
      
      // Use API v2 (MySQL) for invoices
      const invoicesRes = await axios.get(`${API_URL}/api/v2/invoices?${params.toString()}`, { headers, signal });
      
      setInvoices(invoicesRes.data.data || []);
      setFilteredInvoices(invoicesRes.data.data || []);
      
      // Set pagination from server response
      if (invoicesRes.data.total !== undefined) {
        setTotalItems(invoicesRes.data.total);
        setTotalPages(Math.ceil(invoicesRes.data.total / itemsPerPage));
      }
    } catch (error) {
      // 2026-08-08: silent ignore aborted request (bukan error, sengaja di-cancel)
      if (axios.isCancel?.(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error('Error fetching invoices:', error);
      toast.error('Gagal memuat data');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Handle search with debounce
  const [searchTimeout, setSearchTimeout] = useState(null);
  const handleSearchChange = (value) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 500));
  };

  // Pagination - now server-side
  const paginatedInvoices = filteredInvoices;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + paginatedInvoices.length;

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const generateMonthlyInvoices = async () => {
    try {
      // Use V2 API for MySQL
      const response = await axios.post(`${API_URL}/api/v2/invoices/generate-monthly`);
      const data = response.data.data;
      toast.success(`Berhasil generate ${data.generated_count} invoice. ${data.skipped_count} dilewati.`);
      fetchData();
    } catch (error) {
      toast.error('Gagal generate invoice: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSendReminder = async (invoiceId) => {
    try {
      // Keep using old API for reminders
      await axios.post(`${API_URL}/api/invoices/${invoiceId}/send-reminder`);
      toast.success('Reminder berhasil dikirim via Email & WhatsApp');
    } catch (error) {
      toast.error('Gagal mengirim reminder');
    }
  };

  const handleViewInvoice = async (invoice) => {
    try {
      // Use API v2 (MySQL)
      const response = await axios.get(`${API_URL}/api/v2/invoices/${invoice.id}`);
      setSelectedInvoice(response.data.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Gagal memuat detail invoice');
    }
  };

  const handleOpenPrintDialog = (invoice) => {
    setPendingPrintInvoice(invoice);
    setPrintSizeDialogOpen(true);
  };

  // Tampilan saja: invoice LUNAS/PAID -> masa aktif berikutnya (display_due_date dari backend)
  // 2026-10-01 (mode aplikasi lama): selalu tampilkan due_date asli invoice
  const getInvDisplayDue = (inv) => inv?.due_date;

  const handlePrintInvoice = async (size) => {
    try {
      // Get full invoice data with company settings
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/v2/invoices/${pendingPrintInvoice.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedInvoice(response.data.data);
      setPrintSize(size);
      setPrintSizeDialogOpen(false);
      setPrintDialogOpen(true);
    } catch (error) {
      toast.error('Gagal memuat invoice');
    }
  };

  const handleDownloadPDF = () => {
    // Get the print content element
    const printContent = document.querySelector('.print-content');
    
    if (printContent) {
      // Clone the content
      const clonedContent = printContent.cloneNode(true);
      clonedContent.classList.add('cloned-print-content');
      
      // Append to body temporarily
      document.body.appendChild(clonedContent);
      
      // Trigger print
      window.print();
      
      // Remove cloned content after print
      setTimeout(() => {
        document.body.removeChild(clonedContent);
        setPrintDialogOpen(false);
      }, 1000);
    } else {
      window.print();
      setTimeout(() => {
        setPrintDialogOpen(false);
      }, 500);
    }
  };

  const handleDownloadThermal = async (invoice) => {
    try {
      toast.loading('Membuat file thermal...', { id: 'thermal' });
      
      // Get thermal format from backend
      const response = await axios.get(`${API_URL}/api/invoices/${invoice.id}/thermal`);

      if (response.data.success) {
        const thermalText = response.data.data.thermal_text;
        const invoiceNumber = response.data.data.invoice_number;
        
        // Create blob and download as .txt file
        const blob = new Blob([thermalText], { type: 'text/plain;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${invoiceNumber}_thermal.txt`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('File berhasil didownload!\n\nBuka dengan aplikasi: Bluetooth Printer\nAtur font size: LARGE atau 2X', { 
          id: 'thermal',
          duration: 6000
        });
      }
    } catch (error) {
      console.error('Thermal download error:', error);
      toast.error('Gagal download thermal', { id: 'thermal' });
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    // Fetch dampak penghapusan dulu
    const token = localStorage.getItem('token');
    let impactMsg = 'Yakin ingin menghapus invoice ini?';
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API_URL}/api/v2/invoices/${invoiceId}/delete-impact`, { headers });
      const d = res.data?.data;
      if (d) {
        const fmt = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');
        const lines = [
          `Hapus Invoice ${d.invoice.invoice_number} (${d.invoice.customer_name})?`,
          `Status: ${d.invoice.status?.toUpperCase()} | Periode: ${String(d.invoice.period_month||'').padStart(2,'0')}/${d.invoice.period_year||''}`,
          `Total Tagihan: ${fmt(d.invoice.total_amount)}`,
          ''
        ];
        if (d.payments.count > 0) {
          lines.push(`! ${d.payments.count} pembayaran (${fmt(d.payments.total)}) AKAN IKUT TERHAPUS`);
        } else {
          lines.push('Tidak ada pembayaran terkait.');
        }
        if (d.income_transactions.count > 0) {
          lines.push(`! ${d.income_transactions.count} catatan pendapatan (${fmt(d.income_transactions.total)}) AKAN IKUT TERHAPUS`);
        }
        lines.push('');
        lines.push('Tindakan ini akan mempengaruhi Laba Rugi & Neraca. Lanjutkan?');
        impactMsg = lines.join('\n');
      }
    } catch (e) {
      // Kalau gagal fetch impact, tetap pakai konfirmasi default
      console.warn('Gagal fetch delete impact:', e?.message);
    }
    if (window.confirm(impactMsg)) {
      try {
        await axios.delete(`${API_URL}/api/v2/invoices/${invoiceId}`);
        toast.success('Invoice berhasil dihapus');
        fetchData();
      } catch (error) {
        toast.error('Gagal menghapus invoice');
      }
    }
  };


  // Bulk delete functions
  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices(prev => {
      if (prev.includes(invoiceId)) {
        return prev.filter(id => id !== invoiceId);
      } else {
        return [...prev, invoiceId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedInvoices.length === paginatedInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(paginatedInvoices.map(invoice => invoice.id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      if (deleteConfirmText !== 'HAPUS') {
        toast.error('Ketik "HAPUS" untuk konfirmasi');
        return;
      }

      // Use V2 API for bulk delete
      const response = await axios.post(`${API_URL}/api/v2/invoices/bulk-delete`, selectedInvoices);
      toast.success(response.data.message);
      setSelectedInvoices([]);
      setBulkDeleteDialogOpen(false);
      setDeleteConfirmText('');
      fetchData();
    } catch (error) {
      toast.error('Gagal menghapus invoice');
      console.error(error);
    }
  };

  // 2026-06-12: Bulk Tandai Lunas — open dialog, fetch default accounts.
  const handleOpenBulkMarkPaid = async () => {
    if (selectedInvoices.length === 0) {
      toast.error('Pilih minimal 1 invoice terlebih dulu.');
      return;
    }
    setBulkPaidResult(null);
    setBulkPaidDialogOpen(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API_URL}/api/v2/invoices/bulk-mark-paid/default-accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = resp.data?.data || {};
      setBulkPaidAccounts({
        cash: d.cash_accounts || [],
        revenue: d.revenue_accounts || [],
        defaultCash: d.default_cash_account || null,
        defaultRevenue: d.default_revenue_account || null,
      });
      setBulkPaidForm({
        cashCode: d.default_cash_account?.code || '1.11.1',
        revenueCode: d.default_revenue_account?.code || '4.11.1',
        paymentMethod: 'cash',
        paidDateMode: 'due_date',
      });
    } catch (err) {
      console.error('default-accounts fetch failed', err);
      toast.error('Gagal memuat daftar akun. Cek pengaturan Chart of Accounts.');
    }
  };

  const handleConfirmBulkMarkPaid = async () => {
    setBulkPaidLoading(true);
    setBulkPaidResult(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API_URL}/api/v2/invoices/bulk-mark-paid`,
        {
          invoice_ids: selectedInvoices,
          payment_method: bulkPaidForm.paymentMethod,
          cash_account_code: bulkPaidForm.cashCode,
          revenue_account_code: bulkPaidForm.revenueCode,
          paid_date_mode: bulkPaidForm.paidDateMode,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const summary = resp.data?.summary || {};
      setBulkPaidResult(resp.data);
      const msg = `${summary.paid || 0} invoice di-Lunaskan, ${summary.already_had_payment || 0} skip (sudah ada payment), ${summary.failed || 0} gagal.`;
      if ((summary.failed || 0) > 0) {
        toast.warning(msg);
      } else {
        toast.success(msg);
      }
      setSelectedInvoices([]);
      fetchData();
    } catch (err) {
      console.error('bulk mark paid failed', err);
      const errorMsg = err.response?.data?.detail || 'Gagal Tandai Lunas Terpilih';
      toast.error(errorMsg);
    } finally {
      setBulkPaidLoading(false);
    }
  };

  const handleOpenBulkDelete = () => {
    if (selectedInvoices.length === 0) {
      toast.error('Pilih invoice yang akan dihapus');
      return;
    }
    setBulkDeleteDialogOpen(true);
  };

  const handleDeleteAll = async () => {
    try {
      if (deleteAllConfirmText !== 'HAPUS SEMUA') {
        toast.error('Ketik "HAPUS SEMUA" untuk konfirmasi');
        return;
      }

      // Use V2 API for delete all
      const response = await axios.delete(`${API_URL}/api/v2/invoices`);
      toast.success(response.data.message);
      setDeleteAllDialogOpen(false);
      setDeleteAllConfirmText('');
      fetchData();
    } catch (error) {
      toast.error('Gagal menghapus semua invoice');
      console.error(error);
    }
  };


  const handleOpenStatusChange = (invoice) => {
    setSelectedStatusInvoice(invoice);
    setStatusChangeDialogOpen(true);
  };

  const handleChangeStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const currentStatus = selectedStatusInvoice.status?.toUpperCase();
      const newStatus = currentStatus === 'LUNAS' || currentStatus === 'PAID' ? 'pending' : 'paid';
      const updateData = {
        status: newStatus,
        paid_date: newStatus === 'paid' ? new Date().toISOString().split('T')[0] : null,
        payment_method: newStatus === 'paid' ? 'cash' : null
      };

      // Use API v2 (MySQL)
      await axios.put(`${API_URL}/api/v2/invoices/${selectedStatusInvoice.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status invoice berhasil diubah menjadi ${newStatus === 'paid' ? 'LUNAS' : 'BELUM BAYAR'}`);
      setStatusChangeDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error changing status:', error);
      toast.error('Gagal mengubah status invoice');
    }
  };

  const handleOpenEditInvoice = (invoice) => {
    setEditInvoice(invoice);
    setEditFormData({
      created_at: invoice.created_at ? new Date(invoice.created_at).toISOString().split('T')[0] : '',
      due_date: invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : '',
      payment_date: (invoice.paid_date || invoice.payment_date) ? new Date(invoice.paid_date || invoice.payment_date).toISOString().split('T')[0] : '',
      period_month: invoice.period_month ? String(invoice.period_month) : '',
      period_year: invoice.period_year ? String(invoice.period_year) : ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEditInvoice = async () => {
    setEditLoading(true);
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        issue_date: new Date(editFormData.created_at).toISOString(),
        due_date: new Date(editFormData.due_date).toISOString(),
        paid_date: editFormData.payment_date ? new Date(editFormData.payment_date).toISOString() : null
      };
      // FIX 2026-10-04 (Delta #27): admin bisa koreksi periode (bulan/tahun) invoice
      if (editFormData.period_month) updateData.period_month = parseInt(editFormData.period_month, 10);
      if (editFormData.period_year) updateData.period_year = parseInt(editFormData.period_year, 10);

      await axios.put(`${API_URL}/api/invoices/${editInvoice.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice berhasil diupdate');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast.error(error?.response?.data?.detail || 'Gagal mengupdate invoice');
    } finally {
      setEditLoading(false);
    }
  };

  const handleOpenPayment = (invoice) => {
    // Redirect to new payment confirmation page (same as customer portal)
    window.location.href = `/payment/confirmation?invoice_id=${invoice.id}&select=true`;
  };

  // Extend due date handlers
  const handleOpenExtendDialog = (invoice) => {
    setExtendInvoice(invoice);
    // Set default new date to 7 days after current due date
    const currentDueDate = invoice.due_date ? new Date(invoice.due_date) : new Date();
    const newDate = new Date(currentDueDate);
    newDate.setDate(newDate.getDate() + 7);
    setExtendFormData({
      new_due_date: newDate.toISOString().split('T')[0],
      reason: ''
    });
    setExtendDialogOpen(true);
  };

  const handleExtendDueDate = async () => {
    if (!extendInvoice || !extendFormData.new_due_date) {
      toast.error('Tanggal baru wajib diisi');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/invoices/${extendInvoice.id}/extend-due-date`, {
        new_due_date: extendFormData.new_due_date,
        reason: extendFormData.reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tanggal jatuh tempo berhasil diperpanjang');
      setExtendDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error extending due date:', error);
      toast.error(error.response?.data?.detail || 'Gagal memperpanjang tanggal jatuh tempo');
    }
  };

  const handleCreatePayment = async () => {
    if (!selectedPaymentInvoice) return;
    
    setPaymentLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/xendit/create?invoice_id=${selectedPaymentInvoice.id}`
      );
      
      if (response.data.success) {
        setPaymentUrl(response.data.data.payment_url);
        toast.success('Link pembayaran berhasil dibuat!');
      } else {
        toast.error('Gagal membuat link pembayaran');
      }
    } catch (error) {
      console.error('Payment creation error:', error);
      toast.error(error.response?.data?.detail || 'Gagal membuat link pembayaran');
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            margin: 0;
            size: ${printSize === 'a4' ? 'A4 portrait' : printSize === '80mm' ? '80mm auto' : '58mm auto'};
          }
          
          /* Remove browser print headers/footers */
          @page { 
            margin: 0;
          }
          
          /* Hide everything first - more aggressive */
          body * {
            visibility: hidden !important;
          }
          
          /* Hide sidebar, navbar, and all layout elements */
          nav, header, aside, .sidebar, [class*="sidebar"], 
          [class*="navbar"], [class*="nav-"], [role="navigation"],
          button:not(.cloned-print-content button),
          .print-hide, [data-print-hide] {
            display: none !important;
            visibility: hidden !important;
          }
          
          /* Show only cloned print content */
          body > .cloned-print-content,
          body > .cloned-print-content * {
            visibility: visible !important;
          }
          
          body > .cloned-print-content {
            display: block !important;
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            background: white !important;
            padding: ${printSize === 'a4' ? '8mm' : '3mm'} !important;
            margin: 0 !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            transform: scale(${printSize === 'a4' ? '0.95' : '1'}) !important;
            transform-origin: top left !important;
          }
          
          /* Force print backgrounds and colors */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Make all children of cloned content visible */
          .cloned-print-content * {
            display: revert !important;
            visibility: visible !important;
          }
          
          /* Force financial summary to stay right-aligned */
          .cloned-print-content .flex.justify-end {
            display: flex !important;
            justify-content: flex-end !important;
          }
          
          .cloned-print-content .financial-summary-wrapper {
            display: flex !important;
            justify-content: flex-end !important;
            margin-bottom: 24px !important;
          }
          
          .cloned-print-content .financial-summary-box {
            width: 288px !important;
            background-color: #f9fafb !important;
            padding: 16px !important;
            border: 1px solid #d1d5db !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Ensure tables render properly */
          .cloned-print-content table {
            display: table !important;
            width: 100% !important;
            border-collapse: collapse !important;
          }
          
          .cloned-print-content tbody {
            display: table-row-group !important;
          }
          
          .cloned-print-content thead {
            display: table-header-group !important;
          }
          
          .cloned-print-content tr {
            display: table-row !important;
          }
          
          .cloned-print-content td,
          .cloned-print-content th {
            display: table-cell !important;
            vertical-align: top !important;
          }
          
          /* Prevent page breaks inside important sections */
          .cloned-print-content > div {
            page-break-inside: avoid !important;
          }
          
          /* Fit content in one page for A4 */
          ${printSize === 'a4' ? `
          .cloned-print-content {
            font-size: 11px !important;
            line-height: 1.3 !important;
          }
          
          .cloned-print-content h1 {
            font-size: 24px !important;
            margin-bottom: 8px !important;
          }
          
          .cloned-print-content h3 {
            font-size: 12px !important;
            margin-bottom: 8px !important;
          }
          
          .cloned-print-content table {
            font-size: 10px !important;
          }
          
          .cloned-print-content .mb-6 {
            margin-bottom: 12px !important;
          }
          
          .cloned-print-content .mb-4 {
            margin-bottom: 8px !important;
          }
          
          .cloned-print-content .p-3 {
            padding: 6px !important;
          }
          
          .cloned-print-content img {
            max-height: 35px !important;
          }
          ` : ''}
          
          /* Hide buttons in print */
          .cloned-print-content button {
            display: none !important;
          }
          
          /* Ensure white background */
          body {
            background: white !important;
          }
          
          /* Force single page */
          html, body {
            height: auto !important;
            overflow: visible !important;
          }
          
          /* Remove any print watermarks or headers - more specific */
          body > header, body > footer, body > nav, body > aside,
          #root > header, #root > nav, #root > aside,
          [class*="Layout"], [class*="Sidebar"] {
            display: none !important;
            visibility: hidden !important;
          }
        }
      `}</style>
    
    <div className="space-y-4 animate-fade-in p-4 md:p-6 w-full max-w-full overflow-x-auto min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <Card className="border-0 shadow-lg">
        <CardContent className="pt-4 pb-3">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">{t('invoiceManagement')}</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{t('total')} {totalItems} {t('totalInvoices')}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              {selectedInvoices.length > 0 && (
                <Button
                  data-testid="bulk-mark-paid-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto text-sm"
                  onClick={handleOpenBulkMarkPaid}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Tandai Lunas Terpilih ({selectedInvoices.length})
                </Button>
              )}
              {selectedInvoices.length > 0 && (
                <Button
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700 w-full sm:w-auto text-sm"
                  onClick={handleOpenBulkDelete}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Hapus Terpilih ({selectedInvoices.length})
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full sm:w-auto text-sm transition-colors border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
                onClick={() => setDeleteAllDialogOpen(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Hapus Semua
              </Button>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="w-full sm:w-auto text-sm font-medium shadow-sm hover:shadow-md transition-all border-0"
                style={{
                  background: `linear-gradient(135deg, ${branding?.primary_color || '#4F46E5'} 0%, ${branding?.secondary_color || '#7c3aed'} 100%)`,
                  color: getContrastText(branding?.primary_color || '#4F46E5'),
                }}
                data-testid="btn-create-invoice"
              >
                <FileText className="w-4 h-4 mr-2" />
                Buat Tagihan
              </Button>
              <Button
                onClick={() => navigate('/app/invoices/generate')}
                data-testid="btn-generate-bulk"
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <FileText className="w-4 h-4 mr-2" />
                Generate Tagihan
              </Button>
            </div>
          </div>

          {/* Search Bar and Filter */}
          <div className="flex flex-col gap-3">
            {/* Status Filter Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Status:</span>
              <div className="flex items-center border rounded-lg overflow-hidden">
                <Button
                  variant="outline"
                  size="sm"
                  className={`h-8 px-3 text-xs rounded-none border-0 border-r ${statusFilter === 'ALL' ? 'bg-slate-600 text-white hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'}`}
                  onClick={() => setStatusFilter('ALL')}
                >
                  Semua
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={`h-8 px-3 text-xs rounded-none border-0 border-r ${statusFilter === 'paid' ? 'bg-green-500 text-white hover:bg-green-600' : 'text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/30'}`}
                  onClick={() => setStatusFilter('paid')}
                >
                  Lunas
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={`h-8 px-3 text-xs rounded-none border-0 border-r ${statusFilter === 'pending' ? 'bg-amber-500 text-white hover:bg-amber-600' : 'text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/30'}`}
                  onClick={() => setStatusFilter('pending')}
                >
                  Pending
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={`h-8 px-3 text-xs rounded-none border-0 ${statusFilter === 'overdue' ? 'bg-red-500 text-white hover:bg-red-600' : 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30'}`}
                  onClick={() => setStatusFilter('overdue')}
                >
                  Jatuh Tempo
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                className={`h-8 px-3 text-xs rounded-lg border ${susulanFilter ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700' : 'text-purple-600 border-purple-300 hover:bg-purple-50 dark:text-purple-300 dark:border-purple-700 dark:hover:bg-purple-900/30'}`}
                onClick={() => setSusulanFilter(v => !v)}
                data-testid="filter-susulan-btn"
                title="Tampilkan hanya tagihan susulan"
              >
                <span className="mr-1">+</span>Susulan
              </Button>
            </div>

            {/* Owner Filter and Search */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Owner Filter - disabled for owner role */}
              <select
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
                disabled={user?.role?.toLowerCase() === 'owner'}
                className={`px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200 w-full sm:w-auto sm:min-w-[150px] ${user?.role?.toLowerCase() === 'owner' ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <option value="ALL">Semua Owner</option>
                {owners.map(owner => (
                  <option key={owner.id} value={owner.id}>{owner.name}</option>
                ))}
              </select>
              <select
                value={periodMonthFilter}
                onChange={(e) => { setPeriodMonthFilter(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                data-testid="select-invoice-month"
              >
                <option value="ALL">Semua Bulan</option>
                <option value="1">Januari</option><option value="2">Februari</option><option value="3">Maret</option><option value="4">April</option><option value="5">Mei</option><option value="6">Juni</option><option value="7">Juli</option><option value="8">Agustus</option><option value="9">September</option><option value="10">Oktober</option><option value="11">November</option><option value="12">Desember</option>
              </select>
              <select
                value={periodYearFilter}
                onChange={(e) => { setPeriodYearFilter(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                data-testid="select-invoice-year"
              >
                <option value="ALL">Semua Tahun</option>
                {Array.from({length: 5}, (_, i) => new Date().getFullYear() - i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>

              {/* Search Bar */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5 dark:text-slate-500" />
                <Input
                  type="text"
                  placeholder={`${t('search')}...`}
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    handleSearchChange(e.target.value);
                  }}
                  className="pl-10 w-full"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {/* Show entries */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-2 border-b border-slate-100 gap-2 dark:border-slate-700">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-600 dark:text-slate-400">{t('showEntries')}</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-2 py-1 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs dark:border-slate-600"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs text-slate-600 dark:text-slate-400">{t('entries')}</span>
            </div>
            <div className="text-xs text-slate-600 dark:text-slate-400">
              {t('showing')} {startIndex + 1} {t('to')} {endIndex} {t('of')} {totalItems} {t('entries')}
            </div>
          </div>

          <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
            <table className="w-full" style={{ tableLayout: 'auto', minWidth: '900px' }}>
              <colgroup>
                <col style={{ width: '35px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '90px' }} />
                <col style={{ width: '85px' }} />
                <col style={{ width: '85px' }} />
                <col style={{ width: '80px' }} />
                <col style={{ width: '70px' }} />
                <col style={{ width: '80px' }} />
                <col style={{ width: '65px' }} />
                <col style={{ width: '55px' }} />
              </colgroup>
              <thead className="bg-slate-50 border-b border-slate-200 dark:bg-slate-700 dark:border-slate-600">
                <tr>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-100">
                    <input
                      type="checkbox"
                      checked={selectedInvoices.length === paginatedInvoices.length && paginatedInvoices.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer dark:text-indigo-400 dark:border-slate-600"
                    />
                  </th>
                  <th 
                    className="text-left py-2 px-3 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors dark:text-slate-100"
                    onClick={() => handleSort('invoice_number')}
                  >
                    <div className="flex items-center gap-1">
                      {t('invoiceNumber')}
                      {sortConfig.key === 'invoice_number' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="text-left py-2 px-3 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors dark:text-slate-100"
                    onClick={() => handleSort('customer_name')}
                  >
                    <div className="flex items-center gap-1">
                      {t('customer')}
                      {sortConfig.key === 'customer_name' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-100">
                    Owner
                  </th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-100">
                    Cabang
                  </th>
                  <th 
                    className="text-left py-2 px-3 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors dark:text-slate-100"
                    onClick={() => handleSort('total')}
                  >
                    <div className="flex items-center gap-1">
                      {t('amount')}
                      {sortConfig.key === 'total' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="text-left py-2 px-3 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors dark:text-slate-100"
                    onClick={() => handleSort('payment_date')}
                  >
                    <div className="flex items-center gap-1">
                      Tgl Bayar
                      {sortConfig.key === 'payment_date' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="text-left py-2 px-3 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors dark:text-slate-100"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      {t('status')}
                      {sortConfig.key === 'status' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="text-left py-2 px-3 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors dark:text-slate-100"
                    onClick={() => handleSort('due_date')}
                  >
                    <div className="flex items-center gap-1">
                      {t('dueDate')}
                      {sortConfig.key === 'due_date' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="text-left py-2 px-3 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors dark:text-slate-100"
                    onClick={() => handleSort('payment_method')}
                  >
                    <div className="flex items-center gap-1">
                      Metode
                      {sortConfig.key === 'payment_method' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-100">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {paginatedInvoices.map((invoice, index) => {
                  const customer = customers[invoice.customer_id];
                  const _ddue = getInvDisplayDue(invoice); const dueDate = _ddue ? new Date(_ddue) : null;
                  const isOverdue = !invoice.is_paid && dueDate && dueDate < new Date();
                  
                  return (
                    <tr key={index} className="border-b border-slate-100 hover:bg-slate-50 transition-colors dark:border-slate-700/50 dark:hover:bg-slate-700/40">
                      <td className="py-2 px-3 w-10">
                        <input
                          type="checkbox"
                          checked={selectedInvoices.includes(invoice.id)}
                          onChange={() => handleSelectInvoice(invoice.id)}
                          className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer dark:text-indigo-400 dark:border-slate-600"
                        />
                      </td>
                      <td className="py-2 px-3 font-mono text-xs text-slate-700 dark:text-slate-300">{invoice.invoice_number}</td>
                      <td className="py-2 px-3">
                        <div className="font-medium text-xs text-slate-800 dark:text-white">{invoice.customer_name || customer?.name || '-'}</div>
                        <div className="text-[10px] text-slate-500 dark:text-slate-400">{invoice.account_number || invoice.customer_account_number || customer?.account_number || '-'}</div>
                      </td>
                      <td className="py-2 px-3">
                        <span className="text-xs text-slate-700 dark:text-slate-300">{invoice.owner_name || '-'}</span>
                      </td>
                      <td className="py-2 px-3">
                        <span className="text-xs text-slate-600 dark:text-slate-400">{invoice.branch_name || '-'}</span>
                      </td>
                      <td className="py-2 px-3 font-semibold text-xs text-slate-800 dark:text-slate-100">
                        Rp {(invoice.total_amount || invoice.total || invoice.amount || 0).toLocaleString('id-ID')}
                      </td>
                      <td className="py-2 px-3">
                        {invoice.paid_date || invoice.payment_date ? (
                          <div className="flex items-center space-x-1 text-xs text-slate-600 dark:text-slate-400">
                            <Calendar className="w-3 h-3" />
                            <span>{format(new Date(invoice.paid_date || invoice.payment_date), 'dd MMM yyyy')}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic dark:text-slate-500">-</span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex flex-col items-start gap-1">
                          {invoice.status === 'paid' || invoice.is_paid ? (
                            <Badge className="bg-transparent border border-emerald-500 text-emerald-600 text-[10px] py-1 px-2 dark:text-emerald-400">{t('paid')}</Badge>
                          ) : isOverdue ? (
                            <Badge className="bg-transparent border border-slate-400 text-slate-500 text-[10px] py-1 px-2 dark:text-slate-400">{t('overdue')}</Badge>
                          ) : (
                            <Badge className="bg-transparent border border-amber-500 text-amber-600 text-[10px] py-1 px-2 dark:text-amber-400">{t('pending')}</Badge>
                          )}
                          {typeof invoice.notes === 'string' && invoice.notes.trim().toUpperCase().startsWith('[SUSULAN]') && (
                            <Badge className="bg-purple-100 dark:bg-purple-900/30 border border-purple-400 text-purple-700 dark:text-purple-300 text-[9px] py-0.5 px-1.5 font-semibold" title="Tagihan tambahan (susulan) di periode yang sama">
                              Susulan
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        {Number(invoice.total_amount || invoice.total || 0) === 0 ? (
                          <Badge className="bg-purple-100 dark:bg-purple-900/30 border border-purple-300 text-purple-700 dark:text-purple-300 text-[10px] py-1 px-2 font-semibold" title="Paket gratis / unlimited - tidak ada jatuh tempo">
                            Unlimited
                          </Badge>
                        ) : dueDate ? (
                          <div className="flex items-center space-x-1 text-xs text-slate-600 dark:text-slate-400">
                            <Calendar className="w-3 h-3" />
                            <span>{format(dueDate, 'dd MMM yyyy')}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        {invoice.payment_method ? (
                          <Badge 
                            variant="outline"
                            className={`text-[10px] py-1 px-2 whitespace-nowrap bg-transparent ${
                              invoice.payment_method === 'cash' || invoice.payment_method === 'Tunai' 
                                ? 'border-emerald-500 text-emerald-600 hover:bg-emerald-50'
                                : invoice.payment_method === 'xendit' || invoice.payment_method === 'Xendit'
                                ? 'border-blue-500 text-blue-600 hover:bg-blue-50'
                                : invoice.payment_method === 'duitku' || invoice.payment_method === 'Duitku'
                                ? 'border-purple-500 text-purple-600 hover:bg-purple-50'
                                : 'border-slate-400 text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            {invoice.payment_method === 'cash' ? 'Tunai' : 
                             invoice.payment_method === 'xendit' ? 'Xendit' :
                             invoice.payment_method === 'duitku' ? 'Duitku' :
                             invoice.payment_method}
                          </Badge>
                        ) : (
                          <span className="text-xs text-slate-400 italic dark:text-slate-500">-</span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-slate-100 dark:hover:bg-slate-700">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => handleViewInvoice(invoice)}>
                              <Eye className="w-4 h-4 mr-2 text-blue-500" />
                              Lihat Detail
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleOpenEditInvoice(invoice)}>
                              <Edit className="w-4 h-4 mr-2 text-indigo-500" />
                              Edit Tanggal
                            </DropdownMenuItem>
                            {!invoice.is_paid && (
                              <DropdownMenuItem onClick={() => handleOpenExtendDialog(invoice)}>
                                <CalendarPlus className="w-4 h-4 mr-2 text-amber-500" />
                                Perpanjang Jatuh Tempo
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleOpenPrintDialog(invoice)}>
                              <Printer className="w-4 h-4 mr-2 text-green-500" />
                              Print Invoice
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadThermal(invoice)}>
                              <Download className="w-4 h-4 mr-2 text-blue-500" />
                              Download Thermal
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleOpenStatusChange(invoice)}
                              className={invoice.is_paid ? "text-orange-600" : "text-emerald-600"}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              {invoice.is_paid ? 'Ubah ke Belum Bayar' : 'Ubah ke Lunas'}
                            </DropdownMenuItem>
                            {!invoice.is_paid && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleSendReminder(invoice.id)} className="text-indigo-600 dark:text-indigo-400">
                                  <Send className="w-4 h-4 mr-2" />
                                  Kirim Reminder
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleOpenPayment(invoice)} className="text-purple-600 dark:text-purple-400">
                                  <CreditCard className="w-4 h-4 mr-2" />
                                  Bayar via Xendit
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDeleteInvoice(invoice.id)} className="text-red-600 dark:text-red-400">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Hapus Invoice
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50 dark:border-slate-700 dark:bg-slate-900">
            <div className="text-xs text-slate-600 dark:text-slate-400">
              Halaman {currentPage} dari {totalPages} ({totalItems} total)
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 border border-slate-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700/50"
              >
                «
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-slate-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700/50"
              >
                ← Prev
              </button>
              
              <span className="px-3 py-1 bg-indigo-600 text-white rounded text-sm font-medium">
                {currentPage}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-slate-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700/50"
              >
                Next →
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 border border-slate-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700/50"
              >
                »
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Print Size Selection Modal */}
      <Dialog open={printSizeDialogOpen} onOpenChange={setPrintSizeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Pilih Format Printer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-4">
            <button
              onClick={() => handlePrintInvoice('a4')}
              className="w-full flex items-center justify-between p-4 border-2 border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all dark:border-slate-700 dark:hover:bg-blue-900/30"
            >
              <div className="flex items-center space-x-3">
                <span className="text-3xl">📄</span>
                <div className="text-left">
                  <div className="font-bold text-slate-800 dark:text-slate-200">A4 (210 x 297 mm)</div>
                  <div className="text-sm text-slate-600 dark:text-slate-300">Format kertas standar</div>
                </div>
              </div>
              <svg className="w-6 h-6 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button
              onClick={() => handlePrintInvoice('80mm')}
              className="w-full flex items-center justify-between p-4 border-2 border-slate-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all dark:border-slate-700 dark:hover:bg-green-900/30"
            >
              <div className="flex items-center space-x-3">
                <span className="text-3xl">🧾</span>
                <div className="text-left">
                  <div className="font-bold text-slate-800 dark:text-slate-200">80mm (Thermal)</div>
                  <div className="text-sm text-slate-600 dark:text-slate-300">Printer thermal lebar</div>
                </div>
              </div>
              <svg className="w-6 h-6 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button
              onClick={() => handlePrintInvoice('58mm')}
              className="w-full flex items-center justify-between p-4 border-2 border-slate-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all dark:border-slate-700 dark:hover:bg-purple-900/30"
            >
              <div className="flex items-center space-x-3">
                <span className="text-3xl">🧾</span>
                <div className="text-left">
                  <div className="font-bold text-slate-800 dark:text-slate-200">58mm (Thermal)</div>
                  <div className="text-sm text-slate-600 dark:text-slate-300">Printer thermal compact</div>
                </div>
              </div>
              <svg className="w-6 h-6 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Invoice Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detail Invoice</DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold">{selectedInvoice.company_settings?.company_name || ''}</h2>
                    <p className="text-sm mt-2 opacity-90">{selectedInvoice.company_settings?.company_address || ''}</p>
                    <p className="text-sm opacity-90">{selectedInvoice.company_settings?.company_phone || ''}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold">{selectedInvoice.invoice_number}</div>
                    <Badge className={`mt-2 ${selectedInvoice.status === 'LUNAS' ? 'bg-green-500' : 'bg-indigo-500'}`}>
                      {selectedInvoice.status}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-slate-700 mb-2 dark:text-slate-200">Ditagihkan ke:</h3>
                  <div className="bg-slate-50 p-4 rounded-lg dark:bg-slate-900">
                    <p className="font-medium">{selectedInvoice.customer_name}</p>
                    <p className="text-sm text-slate-600 dark:text-slate-300">{selectedInvoice.customer_phone}</p>
                    <p className="text-sm text-slate-600 dark:text-slate-300">{selectedInvoice.customer_address}</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-700 mb-2 dark:text-slate-200">Info Invoice:</h3>
                  <div className="bg-slate-50 p-4 rounded-lg space-y-2 dark:bg-slate-900">
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-300">Tanggal:</span>
                      <span className="font-medium">{format(new Date(selectedInvoice.created_at), 'dd MMM yyyy')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-300">Jatuh Tempo:</span>
                      <span className="font-medium">{format(new Date(getInvDisplayDue(selectedInvoice)), 'dd MMM yyyy')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoice Items */}
              <div>
                <h3 className="font-semibold text-slate-700 mb-2 dark:text-slate-200">Ringkasan Layanan:</h3>
                <table className="w-full border border-slate-200 rounded-lg overflow-hidden dark:border-slate-700">
                  <thead className="bg-slate-100 dark:bg-slate-800">
                    <tr>
                      <th className="text-left p-3 text-sm font-semibold text-slate-700 dark:text-slate-200">Paket Langganan</th>
                      <th className="text-left p-3 text-sm font-semibold text-slate-700 dark:text-slate-200">Jatuh Tempo</th>
                      <th className="text-left p-3 text-sm font-semibold text-slate-700 dark:text-slate-200">Periode Aktif</th>
                      <th className="text-right p-3 text-sm font-semibold text-slate-700 dark:text-slate-200">Jumlah</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Single row with invoice data */}
                    <tr className="border-t border-slate-200 dark:border-slate-700">
                      <td className="p-3">{selectedInvoice.service_name || selectedInvoice.package_name || selectedInvoice.service_plan || (selectedInvoice.items && selectedInvoice.items[0] && selectedInvoice.items[0].description) || 'Layanan Internet'}</td>
                      <td className="p-3 text-sm text-slate-600 dark:text-slate-300">
                        {selectedInvoice.due_date ? format(new Date(getInvDisplayDue(selectedInvoice)), 'dd MMM yyyy') : '-'}
                      </td>
                      <td className="p-3 text-sm text-slate-600 dark:text-slate-300">
                        {selectedInvoice.period_month && selectedInvoice.period_year ? 
                          `${selectedInvoice.period_month}/${selectedInvoice.period_year}` : '-'}
                      </td>
                      <td className="p-3 text-right font-semibold">Rp {(selectedInvoice.total_amount || selectedInvoice.base_amount || 0).toLocaleString('id-ID')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Financial Summary */}
              <div className="bg-slate-50 p-4 rounded-lg dark:bg-slate-900">
                <div className="space-y-2">
                  <div className="flex justify-between text-slate-700 dark:text-slate-200">
                    <span>Subtotal:</span>
                    <span>Rp {(selectedInvoice.base_amount || selectedInvoice.subtotal || 0).toLocaleString('id-ID')}</span>
                  </div>
                  <div className="flex justify-between text-slate-700 dark:text-slate-200">
                    <span>PPN ({selectedInvoice.tax_percentage || 0}%):</span>
                    <span>Rp {(selectedInvoice.tax_amount || 0).toLocaleString('id-ID')}</span>
                  </div>
                  {(() => {
                    const _notes = selectedInvoice.notes || '';
                    const _re = (label) => {
                      const m = _notes.match(new RegExp(label.replace(/[-]/g, '\\-') + ':\\s*Rp\\s*([\\d.,]+)'));
                      return m ? parseFloat(m[1].replace(/[.,]/g, '')) : 0;
                    };
                    const _inst = _re('Biaya Instalasi');
                    const _sewa = _re('Sewa Perangkat');
                    const _lain = _re('Biaya Lain-lain');
                    return (<>
                      {_inst > 0 && (
                        <div className="flex justify-between text-slate-700 dark:text-slate-200" data-testid="row-biaya-instalasi">
                          <span>Biaya Instalasi:</span>
                          <span>Rp {_inst.toLocaleString('id-ID')}</span>
                        </div>
                      )}
                      {_sewa > 0 && (
                        <div className="flex justify-between text-slate-700 dark:text-slate-200" data-testid="row-sewa-perangkat">
                          <span>Sewa Perangkat:</span>
                          <span>Rp {_sewa.toLocaleString('id-ID')}</span>
                        </div>
                      )}
                      {_lain > 0 && (
                        <div className="flex justify-between text-slate-700 dark:text-slate-200" data-testid="row-biaya-lain">
                          <span>Biaya Lain-lain:</span>
                          <span>Rp {_lain.toLocaleString('id-ID')}</span>
                        </div>
                      )}
                    </>);
                  })()}
                  <div className="border-t border-slate-300 pt-2 flex justify-between text-lg font-bold text-slate-900 dark:border-slate-600 dark:text-slate-100">
                    <span>Total:</span>
                    <span>Rp {(selectedInvoice.total_amount || selectedInvoice.total || 0).toLocaleString('id-ID')}</span>
                  </div>
                </div>
              </div>

              {/* Bank Accounts */}
              {selectedInvoice.company_settings?.bank_accounts && selectedInvoice.company_settings.bank_accounts.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-700 mb-2 dark:text-slate-200">Transfer Manual:</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedInvoice.company_settings.bank_accounts.map((bank, index) => (
                      <div key={index} className="bg-slate-50 p-4 rounded-lg dark:bg-slate-900">
                        <p className="font-semibold text-slate-800 dark:text-slate-200">{bank.bank_name}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-300">Atas Nama: {bank.account_name}</p>
                        <p className="text-sm font-mono text-slate-700 dark:text-slate-200">No. Rek: {bank.account_number}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                  Tutup
                </Button>
                <Button
                  className="bg-gradient-to-r from-green-600 to-green-700"
                  onClick={() => {
                    setViewDialogOpen(false);
                    handleOpenPrintDialog(selectedInvoice);
                  }}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Invoice
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Preview Dialog */}
      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col print-dialog-wrapper">
          <DialogHeader className="border-b pb-4 no-print">
            <div className="flex justify-between items-center">
              <DialogTitle className="text-xl font-bold">Print Invoice - {printSize.toUpperCase()}</DialogTitle>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setPrintDialogOpen(false)}
                  className="text-slate-600 dark:text-slate-300"
                >
                  Close
                </Button>
                <Button
                  className="bg-gradient-to-r from-green-600 to-green-700"
                  onClick={handleDownloadPDF}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="flex-1 overflow-y-auto bg-slate-50 p-6 print-content dark:bg-slate-900">
              {/* Print Preview Content */}
              <div className="bg-white shadow-lg mx-auto dark:bg-slate-800" style={{
                width: printSize === 'a4' ? '190mm' : printSize === '80mm' ? '72mm' : '54mm',
                minHeight: 'auto',
                maxHeight: printSize === 'a4' ? '277mm' : 'auto',
                padding: printSize === 'a4' ? '10mm' : '3mm',
                fontSize: printSize === 'a4' ? '9pt' : printSize === '80mm' ? '7pt' : '6pt'
              }}>
                {/* THERMAL FORMAT (80mm) - COMPACT & BOLD */}
                {printSize === '80mm' ? (
                  <div style={{ fontFamily: 'monospace', fontSize: '9pt', fontWeight: 'bold', lineHeight: '1.1', width: '100%', padding: '1.5mm' }}>
                    {/* Header */}
                    <div style={{ textAlign: 'center', marginBottom: '2mm' }}>
                      {selectedInvoice.company_settings?.logo_invoice_url && <img src={selectedInvoice.company_settings.logo_invoice_url} alt={selectedInvoice.company_settings?.company_name || 'Logo'} style={{ maxHeight: '30px', display: 'block', margin: '0 auto 1mm' }} />}
                      <div style={{ fontSize: '8pt', fontWeight: 'bold', marginTop: '0.5mm' }}>
                        {selectedInvoice.company_settings?.company_address}
                      </div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        Telp: {selectedInvoice.company_settings?.company_phone}
                      </div>
                    </div>

                    <div style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }}></div>

                    {/* Invoice & Status */}
                    <div style={{ textAlign: 'center', marginBottom: '2mm' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>INVOICE</div>
                      <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>#{selectedInvoice.invoice_number}</div>
                      <div style={{ border: '1px solid #000', display: 'inline-block', padding: '0.5mm 1.5mm', marginTop: '0.5mm', fontSize: '8.5pt', fontWeight: 'bold' }}>
                        {selectedInvoice.status}
                      </div>
                    </div>

                    <div style={{ borderTop: '1px solid #000', margin: '1.5mm 0' }}></div>

                    {/* Customer Info */}
                    <div style={{ marginBottom: '2mm' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '9pt', marginBottom: '0.5mm' }}>INFORMASI PELANGGAN</div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        ID Pelanggan: {selectedInvoice.customer_account_number || '-'}
                      </div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        Nama: {selectedInvoice.customer_name}
                      </div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        No. HP: {selectedInvoice.customer_phone}
                      </div>
                    </div>

                    {/* Billing Info */}
                    <div style={{ marginBottom: '2mm' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '9pt', marginBottom: '0.5mm' }}>INFORMASI TAGIHAN</div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        Terbit: {format(new Date(selectedInvoice.created_at), 'dd MMM yyyy')}
                      </div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        Jatuh Tempo: {format(new Date(getInvDisplayDue(selectedInvoice)), 'dd MMM yyyy')}
                      </div>
                      {selectedInvoice.status === 'LUNAS' && selectedInvoice.payment_date && (
                        <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                          Tgl Bayar: {format(new Date(selectedInvoice.payment_date), 'dd MMM yyyy')}
                        </div>
                      )}
                    </div>

                    <div style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }}></div>

                    {/* Items */}
                    <div style={{ marginBottom: '2mm' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '8.5pt', marginBottom: '0.5mm' }}>
                        Deskripsi
                      </div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold', marginBottom: '0.3mm' }}>
                        {selectedInvoice.service_name || selectedInvoice.package_name || selectedInvoice.service_plan || (selectedInvoice.items && selectedInvoice.items[0] && selectedInvoice.items[0].description) || 'Layanan Internet'}
                        <div>Rp {(selectedInvoice.total_amount || selectedInvoice.base_amount || 0).toLocaleString('id-ID')}</div>
                      </div>
                    </div>

                    <div style={{ borderTop: '1px solid #000', margin: '1.5mm 0' }}></div>

                    {/* Total */}
                    <div style={{ marginBottom: '2mm' }}>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        Subtotal: Rp {(selectedInvoice.base_amount || selectedInvoice.subtotal || 0).toLocaleString('id-ID')}
                      </div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        PPN ({selectedInvoice.tax_percentage || 0}%): Rp {(selectedInvoice.tax_amount || 0).toLocaleString('id-ID')}
                      </div>
                      {(() => {
                        const _notes = selectedInvoice.notes || '';
                        const _re = (label) => { const m = _notes.match(new RegExp(label.replace(/[-]/g, '\\-') + ':\\s*Rp\\s*([\\d.,]+)')); return m ? parseFloat(m[1].replace(/[.,]/g, '')) : 0; };
                        const _inst = _re('Biaya Instalasi');
                        const _sewa = _re('Sewa Perangkat');
                        const _lain = _re('Biaya Lain-lain');
                        const _st = { fontSize: '8pt', fontWeight: 'bold' };
                        return (<>
                          {_inst > 0 && <div style={_st} data-testid="thermal-instalasi">Biaya Instalasi: Rp {_inst.toLocaleString('id-ID')}</div>}
                          {_sewa > 0 && <div style={_st} data-testid="thermal-sewa">Sewa Perangkat: Rp {_sewa.toLocaleString('id-ID')}</div>}
                          {_lain > 0 && <div style={_st} data-testid="thermal-lain">Biaya Lain-lain: Rp {_lain.toLocaleString('id-ID')}</div>}
                        </>);
                      })()}
                      <div style={{ fontWeight: 'bold', fontSize: '10pt', marginTop: '1mm' }}>
                        TOTAL: Rp {(selectedInvoice.total_amount || selectedInvoice.total || 0).toLocaleString('id-ID')}
                      </div>
                    </div>

                    {selectedInvoice.status === 'LUNAS' && (
                      <div style={{ marginBottom: '2mm', fontSize: '8pt', fontWeight: 'bold' }}>
                        Metode Pembayaran: {selectedInvoice.payment_method || 'Cash'}
                      </div>
                    )}

                    <div style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }}></div>

                    {/* Late Payment Reminder */}
                    {selectedInvoice.status !== 'LUNAS' && (
                      <div style={{ textAlign: 'center', marginBottom: '2mm', fontSize: '8pt', fontWeight: 'bold' }}>
                        <div>Pembayaran paling lambat tanggal</div>
                        <div style={{ fontWeight: 'bold' }}>{format(new Date(getInvDisplayDue(selectedInvoice)), 'dd MMM yyyy')}</div>
                      </div>
                    )}

                    {/* Footer */}
                    <div style={{ textAlign: 'center', fontSize: '8pt', fontWeight: 'bold' }}>
                      <div>Terima kasih atas kepercayaan Anda</div>
                      <div style={{ fontWeight: 'bold', marginTop: '0.5mm' }}>
                        {selectedInvoice.company_settings?.company_name || ''}
                      </div>
                      <div style={{ fontSize: '7.5pt', marginTop: '0.3mm' }}>{format(new Date(), 'dd/MM/yyyy')}</div>
                    </div>
                  </div>
                ) : printSize === '58mm' ? (
                  /* THERMAL FORMAT (58mm) - COMPACT & BOLD */
                  <div style={{ fontFamily: 'monospace', fontSize: '9pt', fontWeight: 'bold', lineHeight: '1.1', width: '100%', padding: '1.5mm' }}>
                    {/* Header */}
                    <div style={{ textAlign: 'center', marginBottom: '2mm' }}>
                      {selectedInvoice.company_settings?.logo_invoice_url && <img src={selectedInvoice.company_settings.logo_invoice_url} alt={selectedInvoice.company_settings?.company_name || 'Logo'} style={{ maxHeight: '25px', display: 'block', margin: '0 auto 1mm' }} />}
                      <div style={{ fontSize: '8pt', fontWeight: 'bold', marginTop: '0.5mm' }}>
                        {selectedInvoice.company_settings?.company_address}
                      </div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        Telp: {selectedInvoice.company_settings?.company_phone}
                      </div>
                    </div>

                    <div style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }}></div>

                    {/* Invoice & Status */}
                    <div style={{ textAlign: 'center', marginBottom: '2mm' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>INVOICE</div>
                      <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>#{selectedInvoice.invoice_number}</div>
                      <div style={{ border: '1px solid #000', display: 'inline-block', padding: '0.5mm 1.5mm', marginTop: '0.5mm', fontSize: '8.5pt', fontWeight: 'bold' }}>
                        {selectedInvoice.status}
                      </div>
                    </div>

                    <div style={{ borderTop: '1px solid #000', margin: '1.5mm 0' }}></div>

                    {/* Customer Info */}
                    <div style={{ marginBottom: '2mm' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '9pt', marginBottom: '0.5mm' }}>INFORMASI PELANGGAN</div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        ID Pelanggan: {selectedInvoice.customer_account_number || '-'}
                      </div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        Nama: {selectedInvoice.customer_name}
                      </div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        Paket: {selectedInvoice.items?.[0]?.description?.split(' ').slice(0, 2).join(' ') || '-'}
                      </div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        Periode: {format(new Date(selectedInvoice.created_at), 'MMMM yyyy')}
                      </div>
                    </div>

                    {/* Billing Info */}
                    <div style={{ marginBottom: '2mm' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '9pt', marginBottom: '0.5mm' }}>INFORMASI TAGIHAN</div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        Terbit: {format(new Date(selectedInvoice.created_at), 'dd MMM yyyy')}
                      </div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        Jatuh Tempo: {format(new Date(getInvDisplayDue(selectedInvoice)), 'dd MMM yyyy')}
                      </div>
                      {selectedInvoice.status === 'LUNAS' && selectedInvoice.payment_date && (
                        <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                          Tgl Bayar: {format(new Date(selectedInvoice.payment_date), 'dd MMM yyyy')}
                        </div>
                      )}
                    </div>

                    <div style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }}></div>

                    {/* Items */}
                    <div style={{ marginBottom: '2mm' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '8.5pt', marginBottom: '0.5mm' }}>
                        Deskripsi
                      </div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold', marginBottom: '0.3mm' }}>
                        {selectedInvoice.service_name || selectedInvoice.package_name || selectedInvoice.service_plan || (selectedInvoice.items && selectedInvoice.items[0] && selectedInvoice.items[0].description) || 'Layanan Internet'}
                        <div>Rp {(selectedInvoice.total_amount || selectedInvoice.base_amount || 0).toLocaleString('id-ID')}</div>
                      </div>
                    </div>

                    <div style={{ borderTop: '1px solid #000', margin: '1.5mm 0' }}></div>

                    {/* Total */}
                    <div style={{ marginBottom: '2mm' }}>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        Subtotal: Rp {(selectedInvoice.base_amount || selectedInvoice.subtotal || 0).toLocaleString('id-ID')}
                      </div>
                      <div style={{ fontSize: '8pt', fontWeight: 'bold' }}>
                        PPN ({selectedInvoice.tax_percentage || 0}%): Rp {(selectedInvoice.tax_amount || 0).toLocaleString('id-ID')}
                      </div>
                      {(() => {
                        const _notes = selectedInvoice.notes || '';
                        const _re = (label) => { const m = _notes.match(new RegExp(label.replace(/[-]/g, '\\-') + ':\\s*Rp\\s*([\\d.,]+)')); return m ? parseFloat(m[1].replace(/[.,]/g, '')) : 0; };
                        const _inst = _re('Biaya Instalasi');
                        const _sewa = _re('Sewa Perangkat');
                        const _lain = _re('Biaya Lain-lain');
                        const _st = { fontSize: '8pt', fontWeight: 'bold' };
                        return (<>
                          {_inst > 0 && <div style={_st} data-testid="thermal-instalasi">Biaya Instalasi: Rp {_inst.toLocaleString('id-ID')}</div>}
                          {_sewa > 0 && <div style={_st} data-testid="thermal-sewa">Sewa Perangkat: Rp {_sewa.toLocaleString('id-ID')}</div>}
                          {_lain > 0 && <div style={_st} data-testid="thermal-lain">Biaya Lain-lain: Rp {_lain.toLocaleString('id-ID')}</div>}
                        </>);
                      })()}
                      <div style={{ fontWeight: 'bold', fontSize: '10pt', marginTop: '1mm' }}>
                        TOTAL: Rp {(selectedInvoice.total_amount || selectedInvoice.total || 0).toLocaleString('id-ID')}
                      </div>
                    </div>

                    {selectedInvoice.status === 'LUNAS' && (
                      <div style={{ marginBottom: '2mm', fontSize: '8pt', fontWeight: 'bold' }}>
                        Metode Pembayaran: {selectedInvoice.payment_method || 'Cash'}
                      </div>
                    )}

                    <div style={{ borderTop: '1px dashed #000', margin: '1.5mm 0' }}></div>

                    {/* Late Payment Reminder */}
                    {selectedInvoice.status !== 'LUNAS' && (
                      <div style={{ textAlign: 'center', marginBottom: '2mm', fontSize: '8pt', fontWeight: 'bold' }}>
                        <div>Pembayaran paling lambat tanggal</div>
                        <div style={{ fontWeight: 'bold' }}>{format(new Date(getInvDisplayDue(selectedInvoice)), 'dd MMM yyyy')}</div>
                      </div>
                    )}

                    {/* Footer */}
                    <div style={{ textAlign: 'center', fontSize: '8pt', fontWeight: 'bold' }}>
                      <div>Terima kasih atas kepercayaan Anda</div>
                      <div style={{ fontWeight: 'bold', marginTop: '0.5mm' }}>
                        {selectedInvoice.company_settings?.company_name || ''}
                      </div>
                      <div style={{ fontSize: '7.5pt', marginTop: '0.3mm' }}>{format(new Date(), 'dd/MM/yyyy')}</div>
                    </div>
                  </div>
                ) : (
                  /* A4 FORMAT - MODERN DESIGN */
                  <>
                {/* Header with Logo and Invoice Title */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    {selectedInvoice.company_settings?.logo_invoice_url && (
                      <img 
                        src={selectedInvoice.company_settings.logo_invoice_url} 
                        alt={selectedInvoice.company_settings?.company_name || 'Logo'} 
                        className="h-16 mb-2"
                        onError={(e) => e.target.style.display='none'}
                      />
                    )}
                    <p className="text-xs text-gray-600 dark:text-slate-300">
                      {selectedInvoice.company_settings?.company_address || 'Address Not Set'}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-slate-300">
                      {selectedInvoice.company_settings?.company_phone || 'Phone Not Set'}
                    </p>
                  </div>
                  <div className="text-right">
                    <h1 className="text-3xl font-bold text-indigo-900 mb-2">INVOICE</h1>
                    <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">
                      {selectedInvoice.invoice_number}
                    </p>
                    <span className={`inline-block px-4 py-2 text-sm font-bold rounded ${selectedInvoice.status === 'LUNAS' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'}`}>
                      {selectedInvoice.status}
                    </span>
                  </div>
                </div>

                {/* Separator Line */}
                <div className="border-b-2 border-indigo-600 mb-4"></div>

                {/* Customer and Invoice Info - Two Columns */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="font-bold text-indigo-900 mb-3 text-sm">DITAGIHKAN KE:</h3>
                    <div className="text-xs space-y-1">
                      <p className="text-gray-600 dark:text-slate-300">ID: {selectedInvoice.customer_account_number || '-'}</p>
                      <p className="font-bold text-gray-900 text-sm dark:text-slate-100">{selectedInvoice.customer_name}</p>
                      <p className="text-gray-700 dark:text-slate-200">{selectedInvoice.customer_address}</p>
                      <p className="text-gray-600 dark:text-slate-300">HP: {selectedInvoice.customer_phone}</p>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-indigo-900 mb-3 text-sm">INFO INVOICE:</h3>
                    <div className="text-xs space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-slate-300">Tanggal:</span>
                        <span className="font-semibold text-gray-900 dark:text-slate-100">{format(new Date(selectedInvoice.created_at), 'dd MMM yyyy')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-slate-300">Jatuh Tempo:</span>
                        <span className="font-semibold text-gray-900 dark:text-slate-100">{format(new Date(getInvDisplayDue(selectedInvoice)), 'dd MMM yyyy')}</span>
                      </div>
                      {selectedInvoice.payment_date && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-slate-300">Tgl Bayar:</span>
                          <span className="font-semibold text-gray-900 dark:text-slate-100">{format(new Date(selectedInvoice.payment_date), 'dd MMM yyyy')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Invoice Items Table - Clean Design */}
                <div className="mb-6">
                  <h3 className="font-bold text-indigo-900 mb-3 text-sm">RINGKASAN LAYANAN</h3>
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-100 border-b-2 border-indigo-600 dark:bg-slate-800">
                        <th className="text-left p-3 font-bold text-gray-900 dark:text-slate-100">Paket Langganan</th>
                        <th className="text-left p-3 font-bold text-gray-900 dark:text-slate-100">Jatuh Tempo</th>
                        <th className="text-left p-3 font-bold text-gray-900 dark:text-slate-100">Periode</th>
                        <th className="text-right p-3 font-bold text-gray-900 dark:text-slate-100">Jumlah</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-200 dark:border-slate-700">
                        <td className="p-3 text-gray-800 dark:text-slate-200">{selectedInvoice.service_name || selectedInvoice.package_name || selectedInvoice.service_plan || (selectedInvoice.items && selectedInvoice.items[0] && selectedInvoice.items[0].description) || 'Layanan Internet'}</td>
                        <td className="p-3 text-gray-600 dark:text-slate-300">
                          {selectedInvoice.due_date ? format(new Date(getInvDisplayDue(selectedInvoice)), 'dd MMM yyyy') : '-'}
                        </td>
                        <td className="p-3 text-gray-600 dark:text-slate-300">{selectedInvoice.period_month ? `${selectedInvoice.period_month}/${selectedInvoice.period_year}` : '30 Hari'}</td>
                        <td className="p-3 text-right font-bold text-gray-900 dark:text-slate-100">Rp {(selectedInvoice.total_amount || selectedInvoice.base_amount || 0).toLocaleString('id-ID')}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Financial Summary - Right Aligned Box */}
                <div className="flex justify-end mb-6 financial-summary-wrapper" style={{ display: 'flex !important', justifyContent: 'flex-end !important', marginBottom: '24px' }}>
                  <div className="w-72 bg-gray-50 p-4 rounded border border-gray-300 financial-summary-box dark:bg-slate-900 dark:border-slate-600" style={{ width: '288px', backgroundColor: '#f9fafb', padding: '16px', borderRadius: '4px', border: '1px solid #d1d5db' }}>
                    <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr>
                          <td style={{ color: '#374151', paddingBottom: '8px' }}>Subtotal:</td>
                          <td style={{ fontWeight: '600', color: '#1f2937', textAlign: 'right', paddingBottom: '8px' }}>Rp {(selectedInvoice.base_amount || selectedInvoice.subtotal || 0).toLocaleString('id-ID')}</td>
                        </tr>
                        <tr>
                          <td style={{ color: '#374151', paddingBottom: '8px' }}>PPN ({selectedInvoice.tax_percentage || 0}%):</td>
                          <td style={{ fontWeight: '600', color: '#1f2937', textAlign: 'right', paddingBottom: '8px' }}>Rp {(selectedInvoice.tax_amount || 0).toLocaleString('id-ID')}</td>
                        </tr>
                        {(() => {
                          const _notes = selectedInvoice.notes || '';
                          const _re = (label) => { const m = _notes.match(new RegExp(label.replace(/[-]/g, '\\-') + ':\\s*Rp\\s*([\\d.,]+)')); return m ? parseFloat(m[1].replace(/[.,]/g, '')) : 0; };
                          const _inst = _re('Biaya Instalasi');
                          const _sewa = _re('Sewa Perangkat');
                          const _lain = _re('Biaya Lain-lain');
                          const _cellL = { color: '#374151', paddingBottom: '8px' };
                          const _cellR = { fontWeight: '600', color: '#1f2937', textAlign: 'right', paddingBottom: '8px' };
                          return (<>
                            {_inst > 0 && (<tr><td style={_cellL}>Biaya Instalasi:</td><td style={_cellR}>Rp {_inst.toLocaleString('id-ID')}</td></tr>)}
                            {_sewa > 0 && (<tr><td style={_cellL}>Sewa Perangkat:</td><td style={_cellR}>Rp {_sewa.toLocaleString('id-ID')}</td></tr>)}
                            {_lain > 0 && (<tr><td style={_cellL}>Biaya Lain-lain:</td><td style={_cellR}>Rp {_lain.toLocaleString('id-ID')}</td></tr>)}
                          </>);
                        })()}
                        <tr>
                          <td colSpan="2" style={{ borderTop: '2px solid #4f46e5', paddingTop: '8px' }}></td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: 'bold', color: '#312e81', fontSize: '16px', paddingTop: '8px' }}>Total:</td>
                          <td style={{ fontWeight: 'bold', color: '#312e81', fontSize: '16px', textAlign: 'right', paddingTop: '8px' }}>Rp {(selectedInvoice.total_amount || selectedInvoice.total || 0).toLocaleString('id-ID')}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Conditional: Transaction Table (if LUNAS) OR Bank Transfer (if BELUM BAYAR) */}
                {selectedInvoice.status === 'LUNAS' ? (
                  /* Transaction Table - For LUNAS Status */
                  <div className="mb-6">
                    <table className="w-full text-xs border border-gray-300 dark:border-slate-600">
                      <thead className="bg-gray-100 dark:bg-slate-800">
                        <tr>
                          <th className="border border-gray-300 p-2 text-left dark:border-slate-600">Transaction Date</th>
                          <th className="border border-gray-300 p-2 text-left dark:border-slate-600">Gateway</th>
                          <th className="border border-gray-300 p-2 text-left dark:border-slate-600">Transaction ID</th>
                          <th className="border border-gray-300 p-2 text-right dark:border-slate-600">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 p-2 dark:border-slate-600">{selectedInvoice.payment_date ? format(new Date(selectedInvoice.payment_date), 'MMMM dd, yyyy') : '-'}</td>
                          <td className="border border-gray-300 p-2 dark:border-slate-600">{selectedInvoice.payment_method || 'Manual'}</td>
                          <td className="border border-gray-300 p-2 dark:border-slate-600">{selectedInvoice.invoice_number}</td>
                          <td className="border border-gray-300 p-2 text-right dark:border-slate-600">Rp. {selectedInvoice.total?.toLocaleString('id-ID')}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="mt-3 text-xs">
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Pembayaran sudah diterima, terima kasih sudah melunasi tagihan anda</li>
                        <li>Jika informasi pada bukti pembayaran ini ada kesalahan, silahkan hubungi kami</li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  /* Bank Transfer - For BELUM BAYAR Status */
                  selectedInvoice.company_settings?.bank_accounts && selectedInvoice.company_settings.bank_accounts.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-bold text-sm mb-3">Transfer Manual</h3>
                      <ul className="list-disc pl-5 space-y-2 text-xs">
                        {selectedInvoice.company_settings.bank_accounts.map((bank, index) => (
                          <li key={index}>
                            <span className="text-blue-600 font-semibold dark:text-blue-400">{bank.bank_name}</span>
                            <span className="mx-1">•</span>
                            <span className="text-green-600 font-semibold dark:text-green-400">{bank.account_name}</span>
                            <span className="mx-1">•</span>
                            <span className="font-mono font-semibold">{bank.account_number}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                )}

                {/* Pembayaran Tunai - Only for BELUM BAYAR */}
                {selectedInvoice.status !== 'LUNAS' && (
                  <div className="mb-6">
                    <h3 className="font-bold text-sm mb-2">Pembayaran Tunai</h3>
                    <div className="text-xs">
                      <p className="font-semibold mb-1">{selectedInvoice.company_settings?.company_name || 'Company Name'}</p>
                      <p>{selectedInvoice.company_settings?.company_address || 'Address Not Set'}</p>
                      <p>{selectedInvoice.company_settings?.company_phone || 'Phone Not Set'}</p>
                    </div>
                  </div>
                )}

                {/* Footer - Compact & Professional */}
                <div className="mt-6 pt-3 border-t border-gray-300 text-center dark:border-slate-600">
                  <p className="text-sm font-bold text-indigo-900 mb-2">
                    Terima Kasih Atas Kepercayaan Anda
                  </p>
                  <p className="text-xs text-gray-500 italic dark:text-slate-400">
                    NOTE: This is computer generated receipt and does not require physical signature.
                  </p>
                </div>
                </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={statusChangeDialogOpen} onOpenChange={setStatusChangeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ubah Status Invoice</DialogTitle>
          </DialogHeader>
          {selectedStatusInvoice && (
            <div className="space-y-4 pt-4">
              <div className="bg-slate-50 p-4 rounded-lg dark:bg-slate-900">
                <p className="text-sm text-slate-600 dark:text-slate-300">Invoice:</p>
                <p className="font-semibold">{selectedStatusInvoice.invoice_number}</p>
                <p className="text-sm text-slate-600 mt-2 dark:text-slate-300">Pelanggan:</p>
                <p className="font-semibold">{selectedStatusInvoice.customer_name}</p>
                <p className="text-sm text-slate-600 mt-2 dark:text-slate-300">Status saat ini:</p>
                <Badge className={`${selectedStatusInvoice.status === 'LUNAS' ? 'bg-green-500' : 'bg-indigo-500'}`}>
                  {selectedStatusInvoice.status}
                </Badge>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 dark:bg-blue-900/30">
                <p className="text-sm text-blue-800">
                  Status akan diubah menjadi: <strong>
                    {selectedStatusInvoice.status === 'LUNAS' ? 'BELUM BAYAR' : 'LUNAS'}
                  </strong>
                </p>
                {selectedStatusInvoice.status !== 'LUNAS' && (
                  <p className="text-xs text-blue-600 mt-1 dark:text-blue-400">
                    Tanggal pembayaran akan diset ke hari ini dengan metode Cash
                  </p>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => setStatusChangeDialogOpen(false)}
                >
                  Batal
                </Button>
                <Button 
                  className={`${selectedStatusInvoice.status === 'LUNAS' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-green-600 hover:bg-green-700'}`}
                  onClick={handleChangeStatus}
                >
                  Ubah Status
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Custom Modal (replaces Radix Dialog for better mobile support) */}
      {editDialogOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-2 sm:p-4"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !editLoading) setEditDialogOpen(false); }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 animate-in fade-in duration-200" aria-hidden="true" />

          {/* Modal */}
          <div
            className="relative w-full max-w-md bg-white rounded-xl shadow-2xl animate-in fade-in zoom-in-95 duration-200 flex flex-col dark:bg-slate-800"
            style={{ maxHeight: 'calc(100dvh - 16px)' }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Edit Tanggal Invoice</h2>
              <button
                type="button"
                onClick={() => !editLoading && setEditDialogOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition dark:text-slate-500 dark:hover:bg-slate-700"
                aria-label="Tutup"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Body (scrollable) */}
            <div className="flex-1 overflow-y-auto px-5 py-4 relative">
              {editLoading && (
                <div className="absolute inset-0 bg-white/90 flex items-center justify-center z-20 rounded-lg">
                  <div className="flex flex-col items-center gap-3">
                    <svg className="animate-spin h-10 w-10 text-indigo-600 dark:text-indigo-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-indigo-600 font-medium dark:text-indigo-400">Memproses perubahan...</p>
                  </div>
                </div>
              )}

              {editInvoice && (
                <div className="space-y-4">
                  <div className="bg-slate-50 p-4 rounded-lg dark:bg-slate-900">
                    <p className="text-sm text-slate-600 dark:text-slate-300">Invoice:</p>
                    <p className="font-semibold">{editInvoice.invoice_number}</p>
                    <p className="text-sm text-slate-600 mt-2 dark:text-slate-300">Pelanggan:</p>
                    <p className="font-semibold">{editInvoice.customer_name}</p>
                    {(editInvoice.is_paid || String(editInvoice.status).toLowerCase() === 'paid') && getInvDisplayDue(editInvoice) && getInvDisplayDue(editInvoice) !== editInvoice.due_date && (
                      <>
                        <p className="text-sm text-slate-600 mt-2 dark:text-slate-300">Masa Aktif Berjalan:</p>
                        <p className="font-semibold text-emerald-600 dark:text-emerald-400">{format(new Date(getInvDisplayDue(editInvoice)), 'dd MMM yyyy')}</p>
                      </>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2 dark:text-slate-200">
                      Tanggal Terbit <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={editFormData.created_at}
                      onChange={(e) => setEditFormData({ ...editFormData, created_at: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2 dark:text-slate-200">
                      Jatuh Tempo Tagihan (batas bayar) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={editFormData.due_date}
                      onChange={(e) => setEditFormData({ ...editFormData, due_date: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                      required
                    />
                    {(editInvoice?.is_paid || String(editInvoice?.status).toLowerCase() === 'paid') && getInvDisplayDue(editInvoice) !== editInvoice?.due_date && (
                      <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Nilai asli tagihan di database. Tanggal yang tampil di tabel &amp; cetakan mengikuti Masa Aktif Berjalan.</p>
                    )}
                  </div>

                  {/* FIX 2026-10-04 (Delta #27): Periode Tagihan (bulan/tahun) */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2 dark:text-slate-200">
                      Periode Tagihan
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        data-testid="edit-invoice-period-month"
                        value={editFormData.period_month}
                        onChange={(e) => setEditFormData({ ...editFormData, period_month: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-800 dark:border-slate-600"
                      >
                        <option value="">Pilih Bulan</option>
                        {['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'].map((m, idx) => (
                          <option key={idx + 1} value={String(idx + 1)}>{m}</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        data-testid="edit-invoice-period-year"
                        min="2000"
                        max="2100"
                        placeholder="Tahun"
                        value={editFormData.period_year}
                        onChange={(e) => setEditFormData({ ...editFormData, period_year: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Label bulan tagihan yang tampil di portal pelanggan (mis. "Tagihan Oktober 2026").</p>
                  </div>

                  {(editInvoice.status === 'LUNAS' || String(editInvoice.status).toLowerCase() === 'paid' || editInvoice.is_paid) && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2 dark:text-slate-200">
                        Tanggal Pembayaran
                      </label>
                      <input
                        type="date"
                        value={editFormData.payment_date}
                        onChange={(e) => setEditFormData({ ...editFormData, payment_date: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                      />
                      <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                        Kosongkan jika ingin menghapus tanggal pembayaran
                      </p>
                    </div>
                  )}

                  <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 dark:bg-yellow-900/30">
                    <p className="text-xs text-yellow-800">
                      💡 <strong>Catatan:</strong> Perubahan tanggal akan mempengaruhi laporan keuangan dan status jatuh tempo invoice
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer (sticky) */}
            <div className="flex-shrink-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-white rounded-b-xl dark:border-slate-700 dark:bg-slate-800">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={editLoading}>
                Batal
              </Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleSaveEditInvoice}
                disabled={editLoading}
              >
                {editLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Menyimpan...
                  </>
                ) : (
                  'Simpan Perubahan'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* Bulk Delete Confirmation Dialog */}
      {/* 2026-06-12: Bulk Tandai Lunas Dialog */}
      <Dialog open={bulkPaidDialogOpen} onOpenChange={setBulkPaidDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-emerald-700 dark:text-emerald-300">Tandai Lunas Terpilih</DialogTitle>
            <DialogDescription>
              Membuat payment record + jurnal akuntansi (DR Kas / CR Pendapatan) untuk {selectedInvoices.length} invoice.
              Invoice yang sudah memiliki payment record akan otomatis di-skip.
            </DialogDescription>
          </DialogHeader>
          {bulkPaidResult ? (
            <div className="space-y-2 text-sm">
              <div className="rounded-md border bg-emerald-50 p-3 text-emerald-900 dark:bg-emerald-900/30">
                <div className="font-semibold">Hasil:</div>
                <div>{bulkPaidResult.message}</div>
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-300">
                Detail per invoice ada di response (lihat console developer).
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Akun Kas (di-debit)</label>
                <select
                  data-testid="bulk-paid-cash-account"
                  className="mt-1 block w-full rounded-md border border-slate-300 p-2 text-sm dark:border-slate-600"
                  value={bulkPaidForm.cashCode}
                  onChange={(e) => setBulkPaidForm((f) => ({ ...f, cashCode: e.target.value }))}
                >
                  {(bulkPaidAccounts.cash || []).map((a) => (
                    <option key={a.id} value={a.code}>{a.code} — {a.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Akun Pendapatan (di-kredit)</label>
                <select
                  data-testid="bulk-paid-revenue-account"
                  className="mt-1 block w-full rounded-md border border-slate-300 p-2 text-sm dark:border-slate-600"
                  value={bulkPaidForm.revenueCode}
                  onChange={(e) => setBulkPaidForm((f) => ({ ...f, revenueCode: e.target.value }))}
                >
                  {(bulkPaidAccounts.revenue || []).map((a) => (
                    <option key={a.id} value={a.code}>{a.code} — {a.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Metode Pembayaran</label>
                <select
                  data-testid="bulk-paid-method"
                  className="mt-1 block w-full rounded-md border border-slate-300 p-2 text-sm dark:border-slate-600"
                  value={bulkPaidForm.paymentMethod}
                  onChange={(e) => setBulkPaidForm((f) => ({ ...f, paymentMethod: e.target.value }))}
                >
                  <option value="cash">Cash</option>
                  <option value="transfer">Transfer</option>
                  <option value="qris">QRIS</option>
                  <option value="ewallet">E-Wallet</option>
                  <option value="other">Lainnya</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Tanggal Bayar</label>
                <select
                  data-testid="bulk-paid-date-mode"
                  className="mt-1 block w-full rounded-md border border-slate-300 p-2 text-sm dark:border-slate-600"
                  value={bulkPaidForm.paidDateMode}
                  onChange={(e) => setBulkPaidForm((f) => ({ ...f, paidDateMode: e.target.value }))}
                >
                  <option value="due_date">Sesuai Tgl Jatuh Tempo masing-masing invoice</option>
                  <option value="today">Hari ini</option>
                </select>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setBulkPaidDialogOpen(false); setBulkPaidResult(null); }}
              disabled={bulkPaidLoading}
            >
              {bulkPaidResult ? 'Tutup' : 'Batal'}
            </Button>
            {!bulkPaidResult && (
              <Button
                data-testid="bulk-paid-confirm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleConfirmBulkMarkPaid}
                disabled={bulkPaidLoading || !bulkPaidForm.cashCode || !bulkPaidForm.revenueCode}
              >
                {bulkPaidLoading ? 'Memproses...' : `Konfirmasi Lunaskan ${selectedInvoices.length} Invoice`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2 dark:text-red-400">
              <Trash2 className="w-5 h-5" />
              Konfirmasi Hapus Invoice
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/30">
              <p className="text-sm text-red-800">
                <strong>⚠️ Peringatan:</strong> Tindakan ini tidak dapat dibatalkan!
              </p>
              <p className="text-sm text-red-700 mt-2 dark:text-red-300">
                Anda akan menghapus <span className="font-bold">{selectedInvoices.length}</span> invoice secara permanen.
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-slate-700 dark:text-slate-200">
                Ketik <span className="font-bold text-red-600 dark:text-red-400">HAPUS</span> untuk mengkonfirmasi:
              </p>
              <Input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Ketik HAPUS"
                className="border-red-300 focus:border-red-500 focus:ring-red-500"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setBulkDeleteDialogOpen(false);
                  setDeleteConfirmText('');
                }}
              >
                Batal
              </Button>
              <Button
                variant="destructive"
                disabled={deleteConfirmText !== 'HAPUS'}
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Hapus {selectedInvoices.length} Invoice
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete ALL Confirmation Dialog */}
      <Dialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-700 flex items-center gap-2 dark:text-red-300">
              <Trash2 className="w-6 h-6" />
              🚨 HAPUS SEMUA INVOICE
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-red-100 border-2 border-red-300 rounded-lg p-4 dark:bg-red-900/40">
              <p className="text-sm text-red-900 font-bold">
                ⚠️⚠️⚠️ PERINGATAN KERAS ⚠️⚠️⚠️
              </p>
              <p className="text-sm text-red-800 mt-2">
                Tindakan ini akan menghapus <span className="font-bold text-lg">SEMUA {filteredInvoices.length} INVOICE</span> di database!
              </p>
              <p className="text-sm text-red-800 mt-2 font-semibold">
                Data tidak dapat dikembalikan setelah dihapus!
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-slate-700 font-medium dark:text-slate-200">
                Ketik <span className="font-bold text-red-700 text-lg dark:text-red-300">HAPUS SEMUA</span> untuk mengkonfirmasi:
              </p>
              <Input
                type="text"
                value={deleteAllConfirmText}
                onChange={(e) => setDeleteAllConfirmText(e.target.value)}
                placeholder="Ketik HAPUS SEMUA"
                className="border-red-400 focus:border-red-600 focus:ring-red-600 font-mono"
              />
              <p className="text-xs text-red-600 italic dark:text-red-400">
                * Harus sama persis: HAPUS SEMUA (huruf besar semua)
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteAllDialogOpen(false);
                  setDeleteAllConfirmText('');
                }}
              >
                Batal
              </Button>
              <Button
                variant="destructive"
                disabled={deleteAllConfirmText !== 'HAPUS SEMUA'}
                onClick={handleDeleteAll}
                className="bg-red-700 hover:bg-red-900"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Hapus SEMUA ({filteredInvoices.length}) Invoice
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center">
              <CreditCard className="w-5 h-5 mr-2 text-purple-600 dark:text-purple-400" />
              Bayar Invoice via Xendit
            </DialogTitle>
          </DialogHeader>
          
          {selectedPaymentInvoice && (
            <div className="space-y-4">
              {/* Invoice Details */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-600 dark:text-slate-300">No. Invoice</p>
                    <p className="font-semibold">{selectedPaymentInvoice.invoice_number}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 dark:text-slate-300">Pelanggan</p>
                    <p className="font-semibold">{selectedPaymentInvoice.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 dark:text-slate-300">Total Tagihan</p>
                    <p className="font-bold text-lg text-purple-600 dark:text-purple-400">
                      Rp {selectedPaymentInvoice.total?.toLocaleString('id-ID') || '0'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600 dark:text-slate-300">Jatuh Tempo</p>
                    <p className="font-semibold">
                      {selectedPaymentInvoice.due_date ? 
                        format(new Date(selectedPaymentInvoice.due_date), 'dd MMM yyyy') : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment URL Section */}
              {!paymentUrl ? (
                <div className="text-center py-8">
                  <div className="mb-4">
                    <CreditCard className="w-16 h-16 mx-auto text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Generate Link Pembayaran</h3>
                  <p className="text-gray-600 mb-6 dark:text-slate-300">
                    Klik tombol di bawah untuk membuat link pembayaran via Xendit
                  </p>
                  <Button
                    onClick={handleCreatePayment}
                    disabled={paymentLoading}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-base"
                  >
                    {paymentLoading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Membuat Link...
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <CreditCard className="w-5 h-5 mr-2" />
                        Buat Link Pembayaran
                      </div>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Success Message */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 dark:bg-green-900/30">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-2 dark:text-green-400" />
                      <p className="text-green-800 font-semibold">Link Pembayaran Berhasil Dibuat!</p>
                    </div>
                  </div>

                  {/* Payment Link */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 dark:bg-slate-800 dark:border-slate-700">
                    <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">Link Pembayaran:</p>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={paymentUrl}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm bg-gray-50 dark:border-slate-600 dark:bg-slate-900"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(paymentUrl);
                          toast.success('Link berhasil disalin!');
                        }}
                        className="whitespace-nowrap"
                      >
                        Salin Link
                      </Button>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <Button
                      onClick={() => window.open(paymentUrl, '_blank')}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-6"
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Buka Halaman Pembayaran
                    </Button>
                    <Button
                      onClick={() => {
                        setPaymentDialogOpen(false);
                        fetchData(); // Refresh data
                      }}
                      variant="outline"
                      className="px-6"
                    >
                      Tutup
                    </Button>
                  </div>

                  {/* Instructions */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-900/30">
                    <p className="text-sm text-blue-800">
                      <strong>Petunjuk:</strong>
                    </p>
                    <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside dark:text-blue-300">
                      <li>Klik "Buka Halaman Pembayaran" untuk melakukan pembayaran</li>
                      <li>Pilih metode pembayaran (QRIS, Transfer Bank, E-Wallet, dll)</li>
                      <li>Status invoice akan otomatis berubah setelah pembayaran berhasil</li>
                      <li>Link pembayaran berlaku selama 24 jam</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Extend Due Date Dialog */}
      <Dialog open={extendDialogOpen} onOpenChange={setExtendDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <CalendarPlus className="w-5 h-5 mr-2 text-amber-500" />
              Perpanjang Jatuh Tempo
            </DialogTitle>
          </DialogHeader>
          {extendInvoice && (
            <div className="space-y-4 mt-4">
              {/* Invoice Info */}
              <div className="bg-slate-50 rounded-lg p-4 dark:bg-slate-900">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-slate-500 dark:text-slate-400">No. Invoice:</span>
                  <span className="font-medium">{extendInvoice.invoice_number}</span>
                  <span className="text-slate-500 dark:text-slate-400">Pelanggan:</span>
                  <span className="font-medium">{extendInvoice.customer_name}</span>
                  <span className="text-slate-500 dark:text-slate-400">Jatuh Tempo Saat Ini:</span>
                  <span className="font-medium text-red-600 dark:text-red-400">
                    {extendInvoice.due_date ? format(new Date(extendInvoice.due_date), 'dd MMM yyyy') : '-'}
                  </span>
                </div>
              </div>

              {/* New Due Date Input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
                  Tanggal Jatuh Tempo Baru <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={extendFormData.new_due_date}
                  onChange={(e) => setExtendFormData({ ...extendFormData, new_due_date: e.target.value })}
                  className="w-full"
                />
              </div>

              {/* Reason Input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
                  Alasan Perpanjangan
                </label>
                <textarea
                  value={extendFormData.reason}
                  onChange={(e) => setExtendFormData({ ...extendFormData, reason: e.target.value })}
                  placeholder="Contoh: Permintaan pelanggan, kendala teknis, dll."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-amber-500 focus:border-transparent dark:border-slate-600"
                  rows={3}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setExtendDialogOpen(false)}
                >
                  Batal
                </Button>
                <Button
                  onClick={handleExtendDueDate}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Perpanjang
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    
      <CreateInvoiceModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => fetchData({ silent: true })}
      />
      <GenerateMonthlyModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onGenerated={() => fetchData({ silent: true })}
      />
</div>
    </>
  );
};

export default Invoices;